import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

/**
 * Request a password reset email. Always shows the same confirmation regardless
 * of whether the email exists in DB — prevents email enumeration.
 */
export default function ForgotPassword() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
            setSent(true);
        } catch (err) {
            // Even on error, we show the same generic message to avoid leaks.
            setSent(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[hsl(220_13%_8%)] text-white">
            <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-white/5">
                <Link
                    to="/login"
                    aria-label={t('auth.back_to_login')}
                    className="inline-flex items-center justify-center h-10 w-10 rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
                    data-testid="forgot-back-link"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1 flex justify-center min-w-0">
                    <Logo variant="light" />
                </div>
                <div className="flex-shrink-0">
                    <LanguageSwitcher variant="dark" compact />
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-8">
                <Card className="w-full max-w-md bg-[hsl(220_13%_10%)] border-[hsl(220_10%_22%)] text-white p-8">
                    {!sent ? (
                        <>
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 mb-4">
                                <Mail className="w-6 h-6 text-[hsl(var(--accent))]" />
                            </div>
                            <h1 className="font-display text-3xl mb-2">{t('auth.forgot_title')}</h1>
                            <p className="text-sm text-white/65 mb-6">{t('auth.forgot_description')}</p>

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label className="text-white/90">{t('auth.email')}</Label>
                                    <Input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="nome@azienda.it"
                                        className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading || !email.trim()}
                                    className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11"
                                    data-testid="forgot-submit-button"
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('auth.sending')}</> : t('auth.forgot_submit')}
                                </Button>
                            </form>

                            <div className="mt-6 pt-5 border-t border-[hsl(220_10%_22%)] text-center">
                                <Link
                                    to="/login"
                                    className="text-xs text-white/55 hover:text-[hsl(var(--accent))] hover:underline transition-colors"
                                    data-testid="forgot-back-to-login"
                                >
                                    ← {t('auth.back_to_login')}
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(145_55%_34%)]/15 mb-4">
                                <CheckCircle2 className="w-6 h-6 text-[hsl(145_55%_50%)]" />
                            </div>
                            <h1 className="font-display text-3xl mb-2">{t('auth.forgot_sent_title')}</h1>
                            <p className="text-sm text-white/70 mb-2">{t('auth.forgot_sent_body_1')}</p>
                            <p className="text-sm text-white/55 mb-6">{t('auth.forgot_sent_body_2')}</p>
                            <Link to="/login" data-testid="forgot-sent-back">
                                <Button variant="outline" className="w-full border-[hsl(220_10%_22%)] bg-transparent text-white hover:bg-white/5 h-11">
                                    ← {t('auth.back_to_login')}
                                </Button>
                            </Link>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
